import { getFormTabErrorCounts } from '../../../../../lib/formTabErrors';
import { zodResolver } from '@hookform/resolvers/zod';
import { BadgeDollarSign, MapPin, Palette } from 'lucide-react';
import { useForm, type FieldErrors } from 'react-hook-form';
import {
  DashboardPageFormActions,
  DashboardPageFormTab,
  DashboardPageFormTabList,
  DashboardPageFormTabPanel,
} from '@shigo-ui/dashboard-ui';
import { Button, Form } from '@shigo-ui/ui';

import { EditorSectionTabs } from '../../../../../components/forms/EditorSectionTabs';
import { useEditorSectionControls } from '../../../../../components/forms/useEditorSection';
import { dashboardT } from '../../../../../lib/messages';
import {
  networkCreateFormSchema,
  networkEditorFormSchema,
  type NetworkFormValue,
} from '../../networkFormSchema';
import { NetworkBrandDetailsFields } from './NetworkBrandDetailsFields/NetworkBrandDetailsFields';
import { NetworkGeneralFields } from './NetworkGeneralFields';
import { NetworkRegionFields } from './NetworkRegionFields';

enum NetworkEditorTab {
  General = 'general',
  BrandDetails = 'brandDetails',
  Us = 'us',
  Eu = 'eu',
}

export function NetworkEditorForm(props: {
  initialValue: NetworkFormValue;
  editing: boolean;
  saving: boolean;
  onCancel: () => void;
  onSubmit: (value: NetworkFormValue) => Promise<void>;
}) {
  'use no memo';
  const [editorSection, setActiveTab] = useEditorSectionControls(
    NetworkEditorTab.General,
    Object.values(NetworkEditorTab),
  );
  const form = useForm<NetworkFormValue>({
    defaultValues: props.initialValue,
    resolver: zodResolver(props.editing ? networkEditorFormSchema : networkCreateFormSchema),
    mode: 'onSubmit',
  });

  function invalidSubmit(errors: FieldErrors<NetworkFormValue>) {
    if (errors.us) setActiveTab(NetworkEditorTab.Us);
    else if (errors.eu) setActiveTab(NetworkEditorTab.Eu);
    else if (errors.brandDetails) setActiveTab(NetworkEditorTab.BrandDetails);
    else setActiveTab(NetworkEditorTab.General);
  }

  const tabErrors = getFormTabErrorCounts(
    form.formState.errors,
    {
      [NetworkEditorTab.BrandDetails]: ['brandDetails'],
      [NetworkEditorTab.Us]: ['us'],
      [NetworkEditorTab.Eu]: ['eu'],
    },
    NetworkEditorTab.General,
  );

  return (
    <Form {...form}>
      <form
        noValidate
        onSubmit={(event) => void form.handleSubmit(props.onSubmit, invalidSubmit)(event)}
      >
        <EditorSectionTabs controls={editorSection}>
          <DashboardPageFormTabList>
            <DashboardPageFormTab
              value={NetworkEditorTab.General}
              errorCount={tabErrors[NetworkEditorTab.General]}
              errorLabel={(count) => dashboardT('common.validationErrors', { count })}
              icon={<BadgeDollarSign className="h-4 w-4" aria-hidden="true" />}
            >
              {dashboardT('networks.tabs.general')}
            </DashboardPageFormTab>
            <DashboardPageFormTab
              value={NetworkEditorTab.BrandDetails}
              errorCount={tabErrors[NetworkEditorTab.BrandDetails]}
              errorLabel={(count) => dashboardT('common.validationErrors', { count })}
              icon={<Palette className="h-4 w-4" aria-hidden="true" />}
            >
              {dashboardT('networks.tabs.brandDetails')}
            </DashboardPageFormTab>
            <DashboardPageFormTab
              value={NetworkEditorTab.Us}
              errorCount={tabErrors[NetworkEditorTab.Us]}
              errorLabel={(count) => dashboardT('common.validationErrors', { count })}
              icon={<MapPin className="h-4 w-4" aria-hidden="true" />}
            >
              {dashboardT('networks.tabs.us')}
            </DashboardPageFormTab>
            <DashboardPageFormTab
              value={NetworkEditorTab.Eu}
              errorCount={tabErrors[NetworkEditorTab.Eu]}
              errorLabel={(count) => dashboardT('common.validationErrors', { count })}
              icon={<MapPin className="h-4 w-4" aria-hidden="true" />}
            >
              {dashboardT('networks.tabs.eu')}
            </DashboardPageFormTab>
          </DashboardPageFormTabList>
          <DashboardPageFormTabPanel value={NetworkEditorTab.General}>
            <NetworkGeneralFields editing={props.editing} />
          </DashboardPageFormTabPanel>
          <DashboardPageFormTabPanel value={NetworkEditorTab.BrandDetails}>
            <NetworkBrandDetailsFields editing={props.editing} />
          </DashboardPageFormTabPanel>
          <DashboardPageFormTabPanel value={NetworkEditorTab.Us}>
            <NetworkRegionFields region="us" />
          </DashboardPageFormTabPanel>
          <DashboardPageFormTabPanel value={NetworkEditorTab.Eu}>
            <NetworkRegionFields region="eu" />
          </DashboardPageFormTabPanel>
        </EditorSectionTabs>
        <DashboardPageFormActions>
          <Button type="button" variant="outline" disabled={props.saving} onClick={props.onCancel}>
            {dashboardT('networks.cancel')}
          </Button>
          <Button type="submit" disabled={props.saving}>
            {dashboardT(props.saving ? 'networks.saving' : 'networks.save')}
          </Button>
        </DashboardPageFormActions>
      </form>
    </Form>
  );
}
